// Brings an already loaded iframe document in line with new HTML by changing
// only the nodes, text and attributes that differ. Setting a new srcdoc would
// navigate the frame instead: blank frame, re-parse, fonts and images again.
// The frame must be same-origin (sandbox="allow-same-origin"); scripts stay
// inert because the sandbox has no allow-scripts.

const ELEMENT_NODE = 1

function syncAttributes(current: Element, next: Element) {
  for (const { name, namespaceURI, localName } of Array.from(current.attributes)) {
    const kept = namespaceURI ? next.hasAttributeNS(namespaceURI, localName) : next.hasAttribute(name)
    if (!kept) current.removeAttributeNode(current.getAttributeNode(name) as Attr)
  }
  for (const { name, namespaceURI, localName, value } of Array.from(next.attributes)) {
    // Namespaced attributes (SVG xlink:href) keep their namespace.
    if (namespaceURI) {
      if (current.getAttributeNS(namespaceURI, localName) !== value) current.setAttributeNS(namespaceURI, name, value)
    } else if (current.getAttribute(name) !== value) {
      current.setAttribute(name, value)
    }
  }
}

// Attributes that identify an element across documents. Children are paired by
// position, except that a keyed element is kept when siblings are inserted or
// removed before it: rewriting an <img>'s src, or recreating it, reloads it.
const KEY_ATTRIBUTES = ['id', 'src'] as const

function nodeKey(node: Node): string | null {
  if (node.nodeType !== ELEMENT_NODE) return null
  const element = node as Element
  const values = KEY_ATTRIBUTES.map((name) => element.getAttribute(name) ?? '')
  return values.some(Boolean) ? [node.nodeName, ...values].join('|') : null
}

function syncChildren(current: Node, next: Node, doc: Document) {
  const nextChildren = Array.from(next.childNodes)
  const nextKeys = nextChildren.map(nodeKey)
  nextChildren.forEach((nextChild, index) => {
    let existing: Node | undefined = current.childNodes[index]
    const key = nextKeys[index]
    if (existing && key && nodeKey(existing) !== key) {
      // Siblings before this keyed element were removed: move it up.
      const later = Array.from(current.childNodes).slice(index + 1).find((node) => nodeKey(node) === key)
      if (later) {
        current.insertBefore(later, existing)
        existing = later
      }
    }
    const existingKey = existing ? nodeKey(existing) : null
    if (existing && existingKey && existingKey !== key && nextKeys.indexOf(existingKey, index + 1) !== -1) {
      // Siblings were inserted before this keyed element: add them, keep it.
      current.insertBefore(doc.importNode(nextChild, true), existing)
      return
    }
    if (existing) syncNode(existing, nextChild, doc)
    else current.appendChild(doc.importNode(nextChild, true))
  })
  while (current.childNodes.length > nextChildren.length) current.removeChild(current.lastChild as ChildNode)
}

function syncNode(current: Node, next: Node, doc: Document) {
  if (current.nodeType !== next.nodeType || current.nodeName !== next.nodeName) {
    current.parentNode?.replaceChild(doc.importNode(next, true), current)
    return
  }
  if (current.nodeType !== ELEMENT_NODE) {
    if (current.nodeValue !== next.nodeValue) current.nodeValue = next.nodeValue
    return
  }
  // nodeType checks, not instanceof: the frame's nodes belong to another realm.
  syncAttributes(current as Element, next as Element)
  syncChildren(current, next, doc)
}

/**
 * Returns false when there is no loaded document to patch or the patch failed
 * (the caller then reloads the frame with the new srcdoc).
 */
export function patchFrameDocument(frame: HTMLIFrameElement | null, html: string): boolean {
  const doc = frame?.contentDocument
  if (!doc?.documentElement || !doc.body) return false
  try {
    const next = new DOMParser().parseFromString(html, 'text/html')
    syncNode(doc.documentElement, next.documentElement, doc)
    return true
  } catch {
    // e.g. an attribute name the parser accepts but setAttribute rejects.
    return false
  }
}
